import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';

import { setAuthTokenProvider } from '@/api/http';
import { isMockAuth, isSupabaseConfigured, supabase } from '@/auth/supabase';

type MockSession = {
  access_token: string;
  user: {
    id: string;
    email?: string;
  };
};

type AuthSession = Session | MockSession;
type AuthUser = User | MockSession['user'];

interface AuthContextValue {
  isConfigured: boolean;
  isMock: boolean;
  isLoading: boolean;
  session: AuthSession | null;
  user: AuthUser | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<AuthSession | null>(null);
  const [isLoading, setIsLoading] = useState(isSupabaseConfigured && !isMockAuth);
  const sessionRef = useRef<AuthSession | null>(null);

  useEffect(() => {
    sessionRef.current = session;
  }, [session]);

  useEffect(() => {
    setAuthTokenProvider(() => sessionRef.current?.access_token ?? null);
    if (isMockAuth) {
      const storedEmail = window.localStorage.getItem('panelforge_mock_user');
      if (storedEmail) {
        const mockSession = {
          access_token: `dev:${storedEmail}`,
          user: { id: storedEmail, email: storedEmail },
        };
        sessionRef.current = mockSession;
        setSession(mockSession);
      }
      setIsLoading(false);
      return;
    }

    if (!supabase) {
      setIsLoading(false);
      return;
    }

    let active = true;
    void supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setIsLoading(false);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      sessionRef.current = nextSession;
      setSession(nextSession);
      void queryClient.invalidateQueries();
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
      setAuthTokenProvider(() => null);
    };
  }, [queryClient]);

  const value = useMemo<AuthContextValue>(() => ({
    isConfigured: isMockAuth || isSupabaseConfigured,
    isMock: isMockAuth,
    isLoading,
    session,
    user: session?.user ?? null,
    signIn: async (email: string, password: string) => {
      if (isMockAuth) {
        const normalizedEmail = email.trim() || 'dev-user';
        window.localStorage.setItem('panelforge_mock_user', normalizedEmail);
        const mockSession = {
          access_token: `dev:${normalizedEmail}`,
          user: { id: normalizedEmail, email: normalizedEmail },
        };
        sessionRef.current = mockSession;
        setSession(mockSession);
        void queryClient.invalidateQueries();
        return;
      }
      if (!supabase) throw new Error('Supabase is not configured');
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    },
    signOut: async () => {
      if (isMockAuth) {
        window.localStorage.removeItem('panelforge_mock_user');
        sessionRef.current = null;
        setSession(null);
        void queryClient.clear();
        return;
      }
      if (!supabase) return;
      await supabase.auth.signOut();
      void queryClient.clear();
    },
  }), [isLoading, queryClient, session]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
